import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container, Header, SpaceBetween } from '@amzn/awsui-components-core';
import { Epic } from '@amzn/awscat-pattern-library-service-model';
import { getText } from '@amzn/awscat-editor';
import { ActionableWarningButton } from '../v2-common/ActionableWarningButton';
import { scrollIntoComfortZone } from '../v2-common/tiptap-editor-integration/Window';
import { SortableDragHandleProps } from '../v2-common/SortableList';
import { useGlobalCursor } from '../v2-hooks/useGlobalCursor';
import { PatternCellField } from './PatternCellField';
import { PatternSuggestionPopover } from './PatternSuggestionPopover';
import { PatternTaskList } from './PatternTaskList';
import { useSuggestionNavigationService } from '../../contexts/SuggestionNavigationServiceContext';
import styles from './PatternEpic.module.scss';

/** Converts a viewport-relative DOMRect to page-absolute coordinates. */
function toPageRect(rect: DOMRect): DOMRect {
  return new DOMRect(rect.x + window.scrollX, rect.y + window.scrollY, rect.width, rect.height);
}

interface PatternEpicProps {
  readonly epic: Epic;
  /** Id of the task that was just added and should start in edit mode, or undefined when idle. */
  readonly newlyAddedTaskId: string | undefined;
  readonly contentId: string;
  readonly hasTitleInlineSuggestions: boolean;
  readonly hasDescriptionInlineSuggestions: boolean;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onAddTask: () => void;
  readonly onReorderTasks: (newOrderIds: readonly string[]) => void;
  readonly dragHandleProps: SortableDragHandleProps;
  readonly isDragging: boolean;
}

export const PatternEpic = memo(function PatternEpic({
  epic,
  newlyAddedTaskId,
  contentId,
  hasTitleInlineSuggestions,
  hasDescriptionInlineSuggestions,
  onEdit,
  onDelete,
  onAddTask,
  onReorderTasks,
  dragHandleProps,
  isDragging,
}: PatternEpicProps) {
  useGlobalCursor('grabbing', isDragging);

  const navigationService = useSuggestionNavigationService();
  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const descriptionButtonRef = useRef<HTMLButtonElement>(null);
  const [popoverTarget, setPopoverTarget] = useState<DOMRect | null>(null);

  // Report which epic containers have navigable inline edits.
  useEffect(() => {
    if (hasTitleInlineSuggestions) {
      navigationService.report({ type: 'epic_title_container', epicId: epic.id });
    }
    if (hasDescriptionInlineSuggestions) {
      navigationService.report({ type: 'epic_description_container', epicId: epic.id });
    }
    return () => {
      navigationService.clear({ type: 'epic_title_container', epicId: epic.id });
      navigationService.clear({ type: 'epic_description_container', epicId: epic.id });
    };
  }, [navigationService, epic.id, hasTitleInlineSuggestions, hasDescriptionInlineSuggestions]);

  const showPopoverFor = useCallback((buttonRef: React.RefObject<HTMLButtonElement | null>) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const pageRect = toPageRect(rect);
      scrollIntoComfortZone(pageRect);
      setPopoverTarget(pageRect);
    }
  }, []);

  // When navigation moves to an item in this epic, show the suggestion popover.
  useEffect(() => {
    return navigationService.on('navigationItemChanged', ({ item }) => {
      if (item.type === 'epic_title_container' && item.epicId === epic.id) {
        showPopoverFor(titleButtonRef);
      } else if (item.type === 'epic_description_container' && item.epicId === epic.id) {
        showPopoverFor(descriptionButtonRef);
      } else {
        setPopoverTarget(null);
      }
    });
  }, [navigationService, epic.id, showPopoverFor]);

  const dismissPopover = useCallback(() => {
    setPopoverTarget(null);
  }, []);
  const handlePopoverEdit = useCallback(() => {
    setPopoverTarget(null);
    onEdit();
  }, [onEdit]);

  const handleTitleButtonClick = useCallback(() => {
    navigationService.moveInto({ type: 'epic_title_container', epicId: epic.id });
    showPopoverFor(titleButtonRef);
  }, [navigationService, epic.id, showPopoverFor]);

  const handleDescriptionButtonClick = useCallback(() => {
    navigationService.moveInto({ type: 'epic_description_container', epicId: epic.id });
    showPopoverFor(descriptionButtonRef);
  }, [navigationService, epic.id, showPopoverFor]);

  const headerActions = (
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="icon" iconName="edit" ariaLabel="Edit epic" onClick={onEdit} />
      <Button variant="icon" iconName="remove" ariaLabel="Delete epic" onClick={onDelete} />
      <span
        ref={dragHandleProps.setActivatorNodeRef}
        {...dragHandleProps.attributes}
        {...dragHandleProps.listeners}
        className={styles.dragHandle}
      >
        <Button variant="icon" iconName="drag-indicator" ariaLabel="Reorder epic" />
      </span>
    </SpaceBetween>
  );

  return (
    <Container
      disableContentPaddings
      header={
        <Header variant="h3" actions={headerActions}>
          <div className={styles.epicTitle}>
            {hasTitleInlineSuggestions && (
              <ActionableWarningButton
                ref={titleButtonRef}
                ariaLabel="AI suggestions available for title"
                onClick={handleTitleButtonClick}
              />
            )}
            <PatternCellField initialValue={epic.title} isReadonly />
          </div>
        </Header>
      }
    >
      <SpaceBetween size="s">
        {getText(epic.description).trim().length > 0 && (
          <div className={styles.descriptionArea}>
            {hasDescriptionInlineSuggestions && (
              <ActionableWarningButton
                ref={descriptionButtonRef}
                ariaLabel="AI suggestions available for description"
                onClick={handleDescriptionButtonClick}
              />
            )}
            <PatternCellField initialValue={epic.description} isReadonly />
          </div>
        )}
        <PatternTaskList
          epic={epic}
          newlyAddedTaskId={newlyAddedTaskId}
          contentId={contentId}
          onAddTask={onAddTask}
          onReorderTasks={onReorderTasks}
        />
      </SpaceBetween>

      {popoverTarget && (
        <PatternSuggestionPopover
          target={popoverTarget}
          onClose={dismissPopover}
          onEdit={handlePopoverEdit}
        />
      )}
    </Container>
  );
});
